import {Box, IconButton, ScaleFade} from '@chakra-ui/react';
import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {ConversationsClientModale} from './ConversationsClientModale';
import {ChatIcon} from '@chakra-ui/icons';
import {BadgeConversationsNonLues} from './BadgeConversationsNonLues';
import {useSidePanelPanier} from '@/components/SidePanelPanier/SidePanelPanierContext';
import {SIDE_PANEL_WIDTH} from '@/components/SidePanelPanier/SidePanelPanier';

/**
 * Bouton flottant pour ouvrir la modale des conversations.
 * Affiché en bas à droite : interface client, prise de commande en mode commercial,
 * et fiche client BO. Animation de scale à l'ouverture/fermeture, badge rouge des messages
 * non lus, décalage quand le side panel panier est ouvert.
 *
 * `clientId` cible explicitement le client de la fiche BO pour l'initiation d'une conversation
 * (hors fiche, la modale retombe sur le client du contexte).
 */
export const ConversationsClientBoutonFlottant: React.FC<{
  clientId?: string;
}> = ({clientId}) => {
  const {t} = useTranslation();
  const modal = useModal(ConversationsClientModale);
  const {isOpen: sidePanelOpen} = useSidePanelPanier();

  // Client courant pour scoper le badge des non-lues : prop (fiche client BO) ou URL (mode commercial
  // `/c/:client_id`). Sans (interface client), badge global = mon unique client. Même résolution que
  // la modale, pour que le badge corresponde à la popin client-scopée.
  const {client_id: clientIdFromUrl} = useParams<{client_id?: string}>();
  const resolvedClientId = clientId ?? clientIdFromUrl;

  const handleOpenModaleConversations = () => {
    NiceModal.show(ConversationsClientModale, {clientId});
  };

  return (
    <Box
      position='fixed'
      bottom={{base: sidePanelOpen ? '160px' : '80px', md: '20px'}}
      right={
        sidePanelOpen
          ? {base: '50px', lg: `calc(${SIDE_PANEL_WIDTH} + 20px)`}
          : '20px'
      }
      zIndex={1400}
      pointerEvents={modal.visible ? 'none' : 'auto'}
      transition='all 0.3s ease'
    >
      {/* Bouton avec animation de scale */}
      <ScaleFade
        initialScale={0.2} // Petite taille au départ
        in={!modal.visible}
        unmountOnExit={false}
        transition={{enter: {duration: 0.4}, exit: {duration: 0.3}}}
      >
        <Box position='relative'>
          <IconButton
            aria-label={t('client.conversations.titre')}
            icon={
              <ChatIcon
                height='24px'
                width='24px'
                color='white'
              />
            }
            bg='blue.main'
            color='white'
            borderRadius='full'
            w='56px'
            h='56px'
            boxShadow='lg'
            _hover={{bg: 'blue.dark'}}
            onClick={handleOpenModaleConversations}
            cursor='pointer'
          />
          {/* Badge rouge avec le nombre de conversations non lues */}
          <BadgeConversationsNonLues clientId={resolvedClientId} />
        </Box>
      </ScaleFade>
    </Box>
  );
};
